import React, { useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Sprout, CreditCard } from "lucide-react";
import PageHero from "../components/PageHero";
import SectionHeader from "../components/SectionHeader";
import { useLanguage } from "../contexts/LanguageContext";
import { IMAGES } from "../data/site";
import { postMembership } from "../lib/api";
import { api } from "../lib/api";

export default function Membership() {
  const { t } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    country: "",
    city: "",
    reason: "",
  });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const [payingFee, setPayingFee] = useState(null);

  const validateForPayment = () => {
    if (!form.full_name.trim()) return "Please enter your full name";
    if (!form.email.trim()) return "Please enter your email";
    if (!form.phone.trim()) return "Please enter your phone number";
    return null;
  };

  const onPayFee = async (feeType) => {
    const err = validateForPayment();
    if (err) {
      toast.error(err);
      return;
    }
    setPayingFee(feeType);
    try {
      const res = await api.post("/membership/checkout", {
        fee_type: feeType,
        origin_url: window.location.origin,
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        country: form.country || undefined,
        city: form.city || undefined,
        reason: form.reason || undefined,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Could not start payment. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Payment failed to start");
      setPayingFee(null);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await postMembership(form);
      toast.success(res.message || t.membership.successMessage);
      setForm({ full_name: "", email: "", phone: "", country: "", city: "", reason: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="membership-page">
      <PageHero
        eyebrow={t.membership.eyebrow}
        title={t.membership.title}
        subtitle={t.membership.subtitle}
        image={IMAGES.community}
      />

      {/* WHO + RESPONSIBILITIES + PRICING */}
      <section className="py-20">
        <div className="gk-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-10">
            <div>
              <SectionHeader eyebrow={t.membership.eyebrow} title={t.membership.whoTitle} />
              <p className="mt-5 text-neutral-700 leading-relaxed">{t.membership.whoBody}</p>
            </div>

            <div>
              <h3 className="font-heading font-bold text-2xl">{t.membership.responsibilitiesTitle}</h3>
              <ul className="mt-5 space-y-3">
                {t.membership.responsibilities.map((r, i) => (
                  <li key={i} className="flex gap-3 text-neutral-700">
                    <span className="mt-1 h-5 w-5 rounded-full bg-[#0B5D1E] text-white flex items-center justify-center flex-shrink-0">
                      <Check size={12} />
                    </span>
                    {r}
                  </li>
                ))}
              </ul>
            </div>

            {/* Pricing — clickable payment buttons */}
            <div className="grid sm:grid-cols-2 gap-4" data-testid="membership-pricing">
              <button
                type="button"
                onClick={() => onPayFee("registration")}
                disabled={!!payingFee}
                data-testid="pay-registration-btn"
                className="text-left rounded-3xl p-8 bg-[#0B5D1E]/5 border-2 border-[#0B5D1E]/20 hover:border-[#0B5D1E] hover:bg-[#0B5D1E]/10 hover:-translate-y-1 hover:shadow-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed group"
              >
                <div className="flex items-center justify-between">
                  <p className="text-xs tracking-[0.25em] font-bold uppercase text-[#0B5D1E]">
                    {t.membership.registrationFee}
                  </p>
                  <CreditCard size={18} className="text-[#0B5D1E]" />
                </div>
                <p className="mt-3 font-heading font-bold text-5xl text-[#0B5D1E]">
                  {t.membership.registrationAmount}
                </p>
                <p className="mt-2 text-sm text-neutral-600">{t.membership.registrationNote}</p>
                <p className="mt-5 inline-flex items-center gap-2 text-sm font-semibold font-heading text-[#0B5D1E] group-hover:gap-3 transition-all">
                  {payingFee === "registration" ? (
                    <><Loader2 size={14} className="animate-spin" /> {t.common.donating}</>
                  ) : (
                    <>Pay {t.membership.registrationAmount} securely →</>
                  )}
                </p>
              </button>

              <button
                type="button"
                onClick={() => onPayFee("monthly")}
                disabled={!!payingFee}
                data-testid="pay-monthly-btn"
                className="text-left rounded-3xl p-8 bg-[#B5651D]/5 border-2 border-[#B5651D]/20 hover:border-[#B5651D] hover:bg-[#B5651D]/10 hover:-translate-y-1 hover:shadow-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed group"
              >
                <div className="flex items-center justify-between">
                  <p className="text-xs tracking-[0.25em] font-bold uppercase text-[#B5651D]">
                    {t.membership.monthlyFee}
                  </p>
                  <CreditCard size={18} className="text-[#B5651D]" />
                </div>
                <p className="mt-3 font-heading font-bold text-5xl text-[#B5651D]">
                  {t.membership.monthlyAmount}
                </p>
                <p className="mt-2 text-sm text-neutral-600">{t.membership.monthlyNote}</p>
                <p className="mt-5 inline-flex items-center gap-2 text-sm font-semibold font-heading text-[#B5651D] group-hover:gap-3 transition-all">
                  {payingFee === "monthly" ? (
                    <><Loader2 size={14} className="animate-spin" /> {t.common.donating}</>
                  ) : (
                    <>Pay {t.membership.monthlyAmount} securely →</>
                  )}
                </p>
              </button>
            </div>

            <p className="text-xs text-neutral-500 -mt-2">
              Fill in your details on the right first, then click a fee to pay securely with Stripe. You will be redirected to a payment portal.
            </p>
          </div>

          {/* FORM */}
          <div className="lg:col-span-5">
            <div className="bg-white rounded-[2rem] border border-black/5 shadow-xl p-8 sticky top-28" data-testid="membership-form-card">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-10 w-10 rounded-2xl bg-[#0B5D1E]/10 text-[#0B5D1E] flex items-center justify-center">
                  <Sprout size={18} />
                </div>
                <h3 className="font-heading font-bold text-2xl">{t.membership.formTitle}</h3>
              </div>
              <p className="text-sm text-neutral-600 mb-6">{t.membership.formSubtitle}</p>

              <form onSubmit={onSubmit} className="space-y-4" data-testid="membership-form">
                <Field label={t.membership.fields.fullName} required>
                  <input
                    type="text"
                    value={form.full_name}
                    onChange={update("full_name")}
                    required
                    className={inputCls}
                    data-testid="membership-input-name"
                  />
                </Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label={t.membership.fields.email} required>
                    <input
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      required
                      className={inputCls}
                      data-testid="membership-input-email"
                    />
                  </Field>
                  <Field label={t.membership.fields.phone} required>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      required
                      className={inputCls}
                      data-testid="membership-input-phone"
                    />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label={t.membership.fields.country}>
                    <input
                      type="text"
                      value={form.country}
                      onChange={update("country")}
                      className={inputCls}
                      data-testid="membership-input-country"
                    />
                  </Field>
                  <Field label={t.membership.fields.city}>
                    <input
                      type="text"
                      value={form.city}
                      onChange={update("city")}
                      className={inputCls}
                      data-testid="membership-input-city"
                    />
                  </Field>
                </div>
                <Field label={t.membership.fields.reason}>
                  <textarea
                    rows={3}
                    value={form.reason}
                    onChange={update("reason")}
                    className={`${inputCls} resize-none`}
                    data-testid="membership-input-reason"
                  />
                </Field>
                <button
                  type="submit"
                  disabled={submitting}
                  className="gk-btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="membership-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      {t.common.sending}
                    </>
                  ) : (
                    t.common.submit
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls =
  "w-full rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm font-body placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] transition";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-neutral-700 font-heading">
        {label} {required && <span className="text-[#B5651D]">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
